require("babel-polyfill");
import axios from "axios";
import { generateRequestHeaders } from "./helpers";

const CONFIG_API_URL = process.env.CONFIG_API_URL;
const OMS_SIMULATOR_API_URL = process.env.OMS_SIMULATOR_API_URL;
const TEMPO_SIMULATOR_API_URL = process.env.TEMPO_SIMULATOR_API_URL;
const HELLO_API_URL = process.env.HELLO_API_URL;

export const authenticate = async (email, password) => {
    const url = `${CONFIG_API_URL}/sessions`;
    const body = { email, password };

    const response = await axios.post(url, body);
    return response.data;
};

export const refreshUserSession = async (refreshToken, accessToken) => {
    const url = `${CONFIG_API_URL}/sessions`;
    const headers = {
        Authorization: `Bearer ${accessToken}`
    };
    const response = await axios.put(url, refreshToken, headers);
    return response.data;
};

export const mfaChallenge = async (session, code, email) => {
    const response = await axios.post(`${CONFIG_API_URL}/sessions/mfa/challenge`, {
        mfaCode: code,
        session,
        email
    });
    return response.data;
};

export const getDestinations = async (projectId, accessToken, queryParams = {}) => {
    let url = `${CONFIG_API_URL}/destinations?projectId=${projectId}`;
    const options = generateRequestHeaders(accessToken);

    if (queryParams.limit) {
        url = `${url}&limit=${queryParams.limit}`;
    };

    if(queryParams.searchTerm){
        url = `${url}&textSearchTerm=${queryParams.searchTerm}`;
    };

    if(queryParams.paginationToken){
        options.headers["x-bluedot-paginationToken"] = queryParams.paginationToken;
    };

    const response = await axios.get(url, options);
    return response;
};

export const getZones = async (projectId, accessToken, queryParams = {}) => {
    let url = `${CONFIG_API_URL}/zones?projectId=${projectId}`;
    const options = generateRequestHeaders(accessToken);

    if (queryParams.limit) {
        url = `${url}&limit=${queryParams.limit}`;
    }

    if(queryParams.searchTerm){
        url = `${url}&textSearchTerm=${queryParams.searchTerm}`;
    }

    if(queryParams.paginationToken){
        options.headers[
            "x-bluedot-paginationToken"
            ] = queryParams.paginationToken;
    }

    const response = await axios.get(url, options);
    return response;
};

export const getZonesCount = async (projectId, accessToken) => {
    const url = `${CONFIG_API_URL}/zones/count?projectId=${projectId}`;
    const options = generateRequestHeaders(accessToken);

    const response = await axios.get(url, options);
    return response.data;
};

export const getProjects = async (accountId, accessToken) => {
    const url = `${CONFIG_API_URL}/projects?accountId=${accountId}`;
    const options = generateRequestHeaders(accessToken);

    const response = await axios.get(url, options);
    return response.data;
};

export const postSingleZone = async (projectId, zone, accessToken) => {
    const url = `${CONFIG_API_URL}/zones`;
    const options = generateRequestHeaders(accessToken);

    const response = await axios.post(url, zone, options);
    return response;
};

export const postZones = async (projectId, zones, accessToken) => {
    const timeout = (ms) => new Promise((res) => setTimeout(res, ms));
    const delay = 75;

    const promises = zones.map(async (zone, index) => {
        delete zone._id;
        zone.projectId = projectId;
        zone.geofeatures.forEach((geo) => delete geo._id);

        await timeout(delay * index);
        return postSingleZone(projectId, zone, accessToken);
    });

    const responses = await Promise.all(promises);
    return responses;
};

const getAccountFeatures = async ({ accountId, accessToken }) => {
    const url = `${CONFIG_API_URL}/accounts/${accountId}/features`;
    const options = generateRequestHeaders(accessToken);

    try {
        let response = await fetch(url, {
            credentials: "same-origin",
            mode: "cors",
            ...options,
        });
        return await response.json();
    } catch (error) {
        console.log(
            `Error occurred during GET of features for accountId ${accountId}: ${error}`
        );
        throw error;
    }
};

const patchAccountFeatures = async ({ accountId, patches, accessToken }) => {
    const url = `${CONFIG_API_URL}/accounts/${accountId}/features`;
    const options = generateRequestHeaders(accessToken);

    try {
        const response = await axios.patch(url, patches, options);
        return response.data;
    } catch (error) {
        console.log(
            `Error occurred during PATCH of features for accountId ${accountId}: ${error}`
        );
        throw error;
    }
};

const createOmsSimulatorOrder = async (orderId, name) => {
    const url = `${OMS_SIMULATOR_API_URL}/create`;
    const payload = {
        orderId,
        name
    };
    try {
        const response = await axios.post(url, payload);
        return response;
    } catch (error) {
        console.log(
            `Error occurred creating OMS Simulator order`
        );
        throw error;
    }
}

const createTempoSimulatorOrder = async (projectId, zoneId, destinationId, orderId, customerName, carMake, carPlate, startDistance, intervalTime, intervalDistance) => {
    const url = `${TEMPO_SIMULATOR_API_URL}`;
    const payload = {
        projectId,
        zoneId,
        orderId,
        type: "start",
        destinationId
    };
    if(customerName || carMake || carPlate){
        payload.customerInformation = {};
        if(customerName){
            payload.customerInformation.customerName = customerName;
        }
        if(carMake){
            payload.customerInformation.vehicleMake = carMake;
        }
        if(carPlate){
            payload.customerInformation.vehicleRegistration = carPlate;
        }
    }

    if(startDistance || intervalTime || intervalDistance){
        payload.simulatorOverride = {};
        if(startDistance){
            payload.simulatorOverride.startDistance = parseInt(startDistance);
        }
        if(intervalTime){
            payload.simulatorOverride.intervalTime = parseInt(intervalTime);
        }
        if(intervalDistance){
            payload.simulatorOverride.intervalDistance = parseInt(intervalDistance);
        }
    }
    try {
        const response = await axios.post(url, payload);
        return response;
    } catch (error) {
        console.log(
            `Error occurred creating Tempo Simulator order`
        );
        throw error;
    }
}

const resetOmsSimulator = async () => {
    const url = `${OMS_SIMULATOR_API_URL}/reset`;
    try{
        const response = await axios.get(url);
        return response
    }catch (error) {
        console.log(
            `Error occurred resetting OMS Simulator order`
        );
        throw error;
    }
}

const resetTempoSimulator = async (orderIds) => {
    const url = `${TEMPO_SIMULATOR_API_URL}`;
    const payload = {
        type: "reset",
        orderIds
    }
    try{
        const response = await axios.post(url, payload);
        return response;
    }catch (error) {
        console.log(
            `Error occurred resetting Tempo Simulator order`
        );
        throw error;
    }
}

/*
    Update reason can be one of "stop", "timeout", or "arriveNow"
 */
const updateSimulatedTempoOrder = async (simulatedOrder, updateReason) => {
    const validUpdateReasons = ["stop", "timeout", "arriveNow"];
    if(!validUpdateReasons.includes(updateReason)){
        throw new Error(`Invalid update reason: ${updateReason}`)
    }

    const url = `${TEMPO_SIMULATOR_API_URL}`;
    const payload = {
        type: updateReason,
        projectId: simulatedOrder.project._id,
        destinationId: simulatedOrder.zone.destinationId,
        orderId: simulatedOrder.orderId,
        zoneId: simulatedOrder.zone._id
    }
    try{
        const response = await axios.post(url, payload);
        return response;
    }catch (error) {
        console.log(
            `Error occurred resetting Tempo Simulator order`
        );
        throw error;
    }
}

const generateHelloScreensUrlCode = async (accessToken, destinationId, projectId, timeToLive = 604800) => {
    const url = `${HELLO_API_URL}/credentials/url/generateCode`
    const options = generateRequestHeaders(accessToken);
    const body = {
        destinationId,
        projectId,
        timeToLive
    }
    const response = await axios.post(url, body, options)
    return response.data.code;
}

const updateUser = async (userId, userBody) => {
    const url = `${CONFIG_API_URL}/users/${userId}`;
    const idToken = localStorage.getItem("idToken");
    const options = generateRequestHeaders(idToken);
    try {
        const response = await axios.put(url, userBody, options);
        return response.data;
    } catch (error) {
        console.log(
            `Error occurred trying to update user with id ${userId}: ${error}`
        );
        throw error;
    }
};

const deleteUser = async (userId) => {
    const url = `${CONFIG_API_URL}/users/${userId}`;
    const idToken = localStorage.getItem("idToken");
    const options = generateRequestHeaders(idToken);
    try {
        await axios.delete(url, options);
    } catch (error) {
        console.log(
            `Error occurred trying to delete user with id ${userId}: ${error}`
        );
        throw error;
    }
};

const api = {
    authenticate,
    getProjects,
    getZones,
    getZonesCount,
    postSingleZone,
    postZones,
    patchAccountFeatures,
    getAccountFeatures,
    createOmsSimulatorOrder,
    createTempoSimulatorOrder,
    resetOmsSimulator,
    resetTempoSimulator,
    updateSimulatedTempoOrder,
    generateHelloScreensUrlCode,
    getDestinations,
    updateUser,
    deleteUser
};

export default api;
